import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';

import { createApiClient, resolveSdkBase } from '../api.js';
import { useConfirm } from '../components/ConfirmDialog.jsx';
import { RefreshIcon } from '../components/icons';

/**
 * SDK version — reframed.
 *
 * There are two numbers that get called "the SDK version" and it's
 * worth keeping them apart:
 *
 *   1. **Bundle version** (deployment-level).  The version baked
 *      into `/sdk/vX.Y.Z/spotlight.min.js` on the CDN, controlled by
 *      whoever runs `scripts/build-sdk.sh` and uploads builds.
 *      This is what customer pages reach for in their <script src>.
 *   2. **Client tag** (tenant-level).  A free-form marker the SDK
 *      fetches on boot and stamps as `X-Spotlight-SDK-Version` on
 *      every outbound request.  Used for cohort analytics — "what
 *      slice of our tenant's users are on version X?" — NOT for
 *      cache-busting.  Bumping it doesn't make browsers refetch the
 *      bundle; it changes the label on the telemetry.
 *
 * The page leads with deployment info (CDN manifest) because that's
 * what most admins are actually looking for.  The client-tag controls
 * hide behind a disclosure: they're an advanced knob, not the headline.
 */
export function SdkVersion() {
  const { getToken } = useAuth();
  const api = useMemo(() => createApiClient(getToken), [getToken]);

  const [manifest, setManifest] = useState(null);
  const [manifestError, setManifestError] = useState(null);
  const [record, setRecord] = useState(null);
  const [recordError, setRecordError] = useState(null);
  const [recordLoading, setRecordLoading] = useState(true);

  useEffect(() => {
    const base = resolveSdkBase();
    fetch(`${base}/manifest.json`, { cache: 'no-store' })
      .then(async (r) => {
        if (!r.ok) throw new Error(`CDN manifest ${r.status}`);
        setManifest(await r.json());
      })
      .catch((e) => setManifestError(e.message));
  }, []);

  const loadRecord = useCallback(async () => {
    setRecordLoading(true);
    setRecordError(null);
    try {
      setRecord(await api('/v1/admin/sdk/version'));
    } catch (e) {
      setRecordError(e.message);
    } finally {
      setRecordLoading(false);
    }
  }, [api]);

  useEffect(() => { loadRecord(); }, [loadRecord]);

  return (
    <div className="page">
      <div className="page__title">
        <div>
          <h1>SDK builds</h1>
          <p className="page__subtitle">
            What's actually on the CDN — the versioned bundles your
            customer pages reach for — and which one <code>latest</code>
            points at right now. Changing the deployed SDK is a
            <code>scripts/build-sdk.sh</code> affair, not a per-tenant
            thing; this page is the window into it.
          </p>
        </div>
      </div>

      {!manifest && !manifestError && (
        <div className="state"><div className="state__title">Reaching the CDN…</div></div>
      )}
      {manifestError && (
        <div className="state state--error">
          <div className="state__title">Couldn't reach CDN manifest</div>
          <div>{manifestError}</div>
        </div>
      )}

      {manifest && (
        <>
          <section className="card sdk-latest">
            <div className="sdk-latest__label">Rolling pointer</div>
            <div className="sdk-latest__version">
              <code>latest</code> → v{manifest.latest}
            </div>
            <div className="sdk-latest__meta">
              Built <TimeAgo iso={manifest.built_at} />
              {manifest.built_at && (
                <> · <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{manifest.built_at}</span></>
              )}
            </div>
            <div className="sdk-latest__meta" style={{ marginTop: 6 }}>
              Customers embedding <code>/sdk/latest/spotlight.min.js</code> get
              this bundle automatically — short CDN TTL ({'<'}60s) means
              a rebuild rolls through in about a minute. Customers
              pinning <code>/sdk/v{manifest.latest}/spotlight.min.js</code>
              stay on this exact version forever (immutable, 1y TTL).
            </div>
          </section>

          <section className="card">
            <h3 className="card__title">Every build on the CDN</h3>
            <p className="card__body">
              Pinned versions are immutable — if you ask for a version
              that isn't here you get a hard 404, never a silent fallback
              to <code>latest</code>.
            </p>
            <ul className="list" style={{ marginTop: 10 }}>
              {(manifest.versions || []).map((v) => (
                <li key={v} className="list__row list__row--muted">
                  <div className="list__primary">
                    <span className="list__name">v{v}</span>
                    <span className="list__meta">
                      <code>/sdk/v{v}/spotlight.min.js</code>
                    </span>
                  </div>
                  {v === manifest.latest && (
                    <span className="badge badge--editor">latest</span>
                  )}
                </li>
              ))}
              {(manifest.versions || []).length === 0 && (
                <li className="list__meta" style={{ padding: 10 }}>
                  The manifest is empty. Run
                  <code> scripts/build-sdk.sh </code> to publish your
                  first bundle.
                </li>
              )}
            </ul>
          </section>
        </>
      )}

      <details className="card" style={{ marginTop: 14 }}>
        <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
          Advanced: tenant client tag
        </summary>
        <p className="card__body" style={{ marginTop: 10 }}>
          Separate from the bundle version: this is a label the SDK
          fetches on boot and stamps onto every outbound request as
          <code> X-Spotlight-SDK-Version</code>. The backend logs it,
          so you can slice analytics ("how many impressions are on tag
          X vs Y"). Changing it <em>does not</em> refresh customer
          bundles — it just relabels the telemetry. Most deployments
          never touch this; it exists for teams who want to cohort
          their own rollouts by tenant.
        </p>

        {recordLoading && (
          <div className="state" style={{ marginTop: 8 }}>
            <div className="state__title">Loading tag…</div>
          </div>
        )}
        {recordError && (
          <div className="state state--error" style={{ marginTop: 8 }}>
            <div className="state__title">Couldn't load tag</div>
            <div>{recordError}</div>
          </div>
        )}
        {record && (
          <>
            <div className="list__meta" style={{ marginTop: 8 }}>
              Current tag: <strong style={{ color: 'var(--text-1)' }}>v{record.version}</strong>
              {record.updated_at && (
                <> · changed <TimeAgo iso={record.updated_at} /></>
              )}
              {record.last_event && (
                <> · last reason <code>{record.last_event}</code></>
              )}
            </div>
            <TagBumpForm api={api} onBumped={loadRecord} current={record.version} />
          </>
        )}
      </details>
    </div>
  );
}

function TagBumpForm({ api, onBumped, current }) {
  const [segment, setSegment] = useState('patch');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [confirmNode, ask] = useConfirm();

  const nextTag = useMemo(() => previewNextVersion(current, segment), [current, segment]);

  function submit(e) {
    e.preventDefault();
    ask({
      title: `Re-tag this tenant's clients from v${current} to v${nextTag}?`,
      body: 'This changes the label on outbound telemetry only — it does NOT refresh customer bundles.',
      confirmLabel: 'Re-tag',
      runOnConfirm: async () => {
        setBusy(true);
        setErr(null);
        try {
          await api('/v1/admin/sdk/version/bump', {
            method: 'POST',
            body: JSON.stringify({
              segment,
              reason: reason.trim() || 'admin_triggered',
            }),
          });
          setReason('');
          onBumped();
        } catch (ex) {
          setErr(ex.message);
          throw ex;
        } finally {
          setBusy(false);
        }
      },
    });
  }

  return (
    <form
      onSubmit={submit}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 2fr auto',
        gap: 12,
        alignItems: 'end',
        marginTop: 14,
      }}
    >
      <label className="field">
        <span className="field__label">Segment</span>
        <select
          className="select"
          value={segment}
          onChange={(e) => setSegment(e.target.value)}
        >
          <option value="patch">patch · v{current} → v{previewNextVersion(current, 'patch')}</option>
          <option value="minor">minor · v{current} → v{previewNextVersion(current, 'minor')}</option>
          <option value="major">major · v{current} → v{previewNextVersion(current, 'major')}</option>
        </select>
      </label>
      <label className="field">
        <span className="field__label">Reason (shows up in audit log)</span>
        <input
          className="input"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="cohort marker for q2 rollout"
        />
      </label>
      <button type="submit" disabled={busy} className="btn btn--secondary">
        <RefreshIcon /> {busy ? 'Re-tagging…' : `Re-tag to v${nextTag}`}
      </button>
      {err && (
        <p style={{ color: 'var(--danger-700)', gridColumn: '1 / -1', margin: 0 }}>
          {err}
        </p>
      )}
      {confirmNode}
    </form>
  );
}

function previewNextVersion(current, segment) {
  if (!current) return '—';
  const m = /^(\d+)\.(\d+)\.(\d+)$/.exec(current);
  if (!m) return '?';
  let [, major, minor, patch] = m.map(Number);
  if (segment === 'major') return `${major + 1}.0.0`;
  if (segment === 'minor') return `${major}.${minor + 1}.0`;
  return `${major}.${minor}.${patch + 1}`;
}

function TimeAgo({ iso }) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const seconds = Math.round((Date.now() - d.getTime()) / 1000);
    if (seconds < 60) return <>just now</>;
    if (seconds < 3600) return <>{Math.round(seconds / 60)}m ago</>;
    if (seconds < 86400) return <>{Math.round(seconds / 3600)}h ago</>;
    if (seconds < 86400 * 7) return <>{Math.round(seconds / 86400)}d ago</>;
    return <>{d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</>;
  } catch {
    return <>{iso}</>;
  }
}

/* eslint-disable-next-line react-refresh/only-export-components */
const SDK_CSS = `
  .sdk-latest {
    display: flex;
    flex-direction: column;
    gap: 4px;
    background: linear-gradient(180deg, var(--brand-50) 0%, var(--surface-0) 100%);
    border-color: var(--brand-100);
  }
  .sdk-latest__label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--brand-700);
  }
  .sdk-latest__version {
    font-family: var(--font-mono);
    font-size: 24px;
    font-weight: 700;
    color: var(--text-1);
    letter-spacing: -0.01em;
  }
  .sdk-latest__meta {
    font-size: 13px;
    color: var(--text-3);
  }
`;
// Inject once at module load — route is mounted on the Shell which
// already has the global stylesheet, so this is the only place we
// need the SDK-specific bits.
if (typeof document !== 'undefined' && !document.getElementById('sdk-version-css')) {
  const style = document.createElement('style');
  style.id = 'sdk-version-css';
  style.textContent = SDK_CSS;
  document.head.appendChild(style);
}
